import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, CheckCheck, ImagePlus, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button.tsx';
import { Textarea } from '../ui/textarea.tsx';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import userTeamService from '../../services/userTeamService';
import {
  isSupportChatTypingFresh,
  markSupportChatRead,
  notifySupportChat,
  sendSupportChatMessage,
  setSupportChatTyping,
  subscribeToSupportChatMessages,
  subscribeToSupportChatThreadDoc,
  supportChatTimestampMillis,
  uploadSupportChatAttachment,
} from '../../services/supportChatService';
import SupportChatAttachmentImage from './SupportChatAttachmentImage';

const MAX_PENDING_ATTACHMENTS = 5;
const TYPING_HEARTBEAT_MS = 3500;

function formatMessageTime(createdAt) {
  const date = createdAt?.toDate ? createdAt.toDate() : createdAt ? new Date(createdAt) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return time;
  return `${date.toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${time}`;
}

/**
 * Message thread + composer for one support chat thread. Used inside both the
 * floating panel and the expanded modal. viewer: 'user' | 'admin'.
 */
const ChatPanel = ({ threadEmail, viewer, user, active = true, unreadCount = 0 }) => {
  const { isDark } = useTheme();
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState('');
  const [pending, setPending] = useState([]); // [{ file, previewUrl }]
  const [sending, setSending] = useState(false);
  const [thread, setThread] = useState(null); // read receipts + typing state
  const [now, setNow] = useState(() => Date.now());
  const listRef = useRef(null);
  const contentRef = useRef(null);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const lastTypingWriteRef = useRef(0);
  /** When true, keep the list pinned as content grows (images, typing, new msgs). */
  const stickToBottomRef = useRef(true);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    const list = listRef.current;
    if (!list) return;
    if (behavior === 'smooth') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }
    list.scrollTop = list.scrollHeight;
  }, []);

  useEffect(() => {
    if (!threadEmail) return undefined;
    setMessages(null);
    stickToBottomRef.current = true;
    const unsubscribe = subscribeToSupportChatMessages(threadEmail, setMessages);
    return unsubscribe;
  }, [threadEmail]);

  useEffect(() => {
    if (!threadEmail) return undefined;
    setThread(null);
    return subscribeToSupportChatThreadDoc(threadEmail, setThread);
  }, [threadEmail]);

  // Stop broadcasting typing when the panel closes or unmounts mid-compose.
  useEffect(() => () => {
    if (lastTypingWriteRef.current > 0) {
      lastTypingWriteRef.current = 0;
      setSupportChatTyping(threadEmail, viewer, false);
    }
  }, [threadEmail, viewer]);

  const counterpartTypingAt = viewer === 'admin' ? thread?.userTypingAt : thread?.adminTypingAt;
  const counterpartTyping = isSupportChatTypingFresh(counterpartTypingAt, now);

  // Firestore never pushes an update when a typing heartbeat merely ages out,
  // so tick a local clock while one is fresh to hide the bubble afterwards.
  useEffect(() => {
    if (!isSupportChatTypingFresh(counterpartTypingAt, Date.now())) return undefined;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1500);
    return () => clearInterval(interval);
  }, [counterpartTypingAt]);

  // Re-open / expand: always pin to the latest message.
  useEffect(() => {
    if (!active) return;
    stickToBottomRef.current = true;
  }, [active]);

  // Scroll when messages/typing change if the user is (or should be) at the bottom.
  useEffect(() => {
    if (!active || messages === null || !stickToBottomRef.current) return undefined;
    scrollToBottom('auto');
    const raf = requestAnimationFrame(() => scrollToBottom('auto'));
    return () => cancelAnimationFrame(raf);
  }, [active, messages, messages?.length, counterpartTyping, scrollToBottom]);

  const messagesLoaded = messages !== null;

  // Content height can grow after paint (attachment images, fonts). ResizeObserver
  // on an inner wrapper catches that; observing the overflow container alone does not.
  useEffect(() => {
    if (!active || !messagesLoaded) return undefined;
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) scrollToBottom('auto');
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [active, messagesLoaded, scrollToBottom]);

  // Let the user scroll up to read history without being yanked back down.
  useEffect(() => {
    if (!active || !messagesLoaded) return undefined;
    const list = listRef.current;
    if (!list) return undefined;
    const onScroll = () => {
      const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
      stickToBottomRef.current = distanceFromBottom <= 80;
    };
    list.addEventListener('scroll', onScroll, { passive: true });
    return () => list.removeEventListener('scroll', onScroll);
  }, [active, messagesLoaded]);

  // Mark read while the panel is open and the window is focused. This also
  // resets the backend email throttle for this direction.
  useEffect(() => {
    if (!active || !threadEmail || !(unreadCount > 0)) return undefined;
    const markIfFocused = () => {
      if (document.hasFocus()) markSupportChatRead(threadEmail, viewer);
    };
    markIfFocused();
    window.addEventListener('focus', markIfFocused);
    return () => window.removeEventListener('focus', markIfFocused);
  }, [active, threadEmail, viewer, unreadCount]);

  useEffect(() => () => {
    // Revoke preview URLs on unmount
    setPending((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return prev;
    });
  }, []);

  const addFiles = useCallback((files) => {
    const images = Array.from(files || []).filter((f) => f.type?.startsWith('image/'));
    if (!images.length) return;
    setPending((prev) => {
      const room = MAX_PENDING_ATTACHMENTS - prev.length;
      if (room <= 0) {
        toast.error(`Maximum ${MAX_PENDING_ATTACHMENTS} images per message`);
        return prev;
      }
      return [
        ...prev,
        ...images.slice(0, room).map((file) => ({ file, previewUrl: URL.createObjectURL(file) })),
      ];
    });
  }, []);

  const removePending = (index) => {
    setPending((prev) => {
      const item = prev[index];
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handlePaste = (event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const files = items
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (files.length) {
      event.preventDefault();
      addFiles(files);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    addFiles(event.dataTransfer?.files);
  };

  const canSend = !sending && (text.trim().length > 0 || pending.length > 0);

  const resizeComposer = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    resizeComposer();
  }, [text, resizeComposer]);

  const handleTextChange = (event) => {
    const value = event.target.value;
    setText(value);
    if (!threadEmail) return;
    if (!value.trim()) {
      if (lastTypingWriteRef.current > 0) {
        lastTypingWriteRef.current = 0;
        setSupportChatTyping(threadEmail, viewer, false);
      }
      return;
    }
    if (Date.now() - lastTypingWriteRef.current >= TYPING_HEARTBEAT_MS) {
      lastTypingWriteRef.current = Date.now();
      setSupportChatTyping(threadEmail, viewer, true);
    }
  };

  const handleSend = async () => {
    if (!canSend || !threadEmail || !user?.email) return;
    setSending(true);
    try {
      const attachments = [];
      for (const item of pending) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await uploadSupportChatAttachment(item.file, threadEmail);
        attachments.push(uploaded);
      }
      await sendSupportChatMessage({
        threadEmail,
        sender: viewer,
        senderEmail: user.email,
        senderName: userTeamService.getUserName(user.email) || user.email,
        text,
        attachments,
        // Admin-initiated threads have no userName until the user replies —
        // stamp it from the team cache so the inbox shows a name immediately.
        threadUserName:
          viewer === 'admin' ? userTeamService.getUserName(threadEmail) || threadEmail : undefined,
      });
      pending.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      setPending([]);
      setText('');
      // sendSupportChatMessage clears the typing field in the same batch.
      lastTypingWriteRef.current = 0;
      textareaRef.current?.focus();
      // Email the other party — throttled server-side; failures are non-fatal.
      notifySupportChat(threadEmail, viewer === 'admin' ? 'to-user' : 'to-admin').catch((err) => {
        console.warn('Support chat notify failed:', err.message);
      });
    } catch (error) {
      console.error('Send support chat message error:', error);
      toast.error(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const emptyText = useMemo(
    () =>
      viewer === 'admin'
        ? 'No messages in this conversation yet.'
        : 'Start a conversation — we will reply here and you will also be notified by email.',
    [viewer]
  );

  // Delivered/read status shown on the viewer's most recent message only.
  const lastOwnMessageId = useMemo(() => {
    if (!messages) return null;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].sender === viewer) return messages[i].id;
    }
    return null;
  }, [messages, viewer]);

  const counterpartReadMillis = supportChatTimestampMillis(
    viewer === 'admin' ? thread?.lastReadByUserAt : thread?.lastReadByAdminAt
  );

  const renderOwnMessageStatus = (msg) => {
    if (msg.id !== lastOwnMessageId) return null;
    if (msg.pending || !msg.createdAt) {
      return <span className="inline-flex items-center gap-0.5 ml-1">· Sending…</span>;
    }
    const isRead =
      counterpartReadMillis > 0
      && counterpartReadMillis >= supportChatTimestampMillis(msg.createdAt);
    return (
      <span className="inline-flex items-center gap-0.5 ml-1">
        ·
        {isRead ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />}
        {isRead ? 'Read' : 'Delivered'}
      </span>
    );
  };

  return (
    <div
      className="flex-1 min-h-0 flex flex-col"
      onDrop={handleDrop}
      onDragOver={(event) => event.preventDefault()}
    >
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
        <div ref={contentRef} className="min-h-full space-y-3">
          {messages === null ? (
            <div className="min-h-[12rem] flex items-center justify-center text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading messages…
            </div>
          ) : messages.length === 0 ? (
            <div className="min-h-[12rem] flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
              {emptyText}
            </div>
          ) : (
            messages.map((msg) => {
              const isMine = msg.sender === viewer;
              return (
                <div key={msg.id} className={cn('flex', isMine ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cn(
                      'max-w-[85%] rounded-lg px-3 py-2 text-xs',
                      isMine
                        ? 'bg-muted text-foreground'
                        : (isDark ? 'bg-neutral-200 text-neutral-900' : 'bg-neutral-100 text-neutral-900')
                    )}
                  >
                    <div className="space-y-2">
                      {(msg.attachments || []).map((att) => (
                        <SupportChatAttachmentImage
                          key={att.fileId}
                          threadEmail={threadEmail}
                          fileId={att.fileId}
                          alt={att.fileName || 'Screenshot'}
                          scrollRootRef={listRef}
                        />
                      ))}
                      {msg.text ? (
                        <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                      ) : null}
                    </div>
                    <div
                      className={cn(
                        'mt-1 text-[11px] flex items-center flex-wrap gap-x-1',
                        // Own bubbles are bg-muted (theme token); the other side is a fixed light neutral in both themes.
                        isMine ? 'text-foreground/70' : 'text-neutral-600'
                      )}
                    >
                      <span>
                        {msg.senderName ? `${msg.senderName} · ` : ''}
                        {formatMessageTime(msg.createdAt)}
                        {msg.via === 'email' ? ' · by email' : ''}
                      </span>
                      {isMine ? renderOwnMessageStatus(msg) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {counterpartTyping && (
            <div className="flex justify-start">
              <div
                className={cn(
                  'rounded-lg px-3 py-2.5',
                  isDark ? 'bg-neutral-200 text-neutral-900' : 'bg-neutral-100 text-neutral-900',
                )}
                aria-label="Typing…"
              >
                <div className="flex items-center gap-1.5">
                  {[0, 150, 300].map((delay) => (
                    <span
                      key={delay}
                      className={cn(
                        'h-2 w-2 rounded-full animate-bounce',
                        isDark ? 'bg-neutral-700' : 'bg-neutral-500',
                      )}
                      style={{ animationDelay: `${delay}ms` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <div className="shrink-0 border-t border-border px-3 py-2 space-y-2">
        {pending.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {pending.map((item, index) => (
              <div key={item.previewUrl} className="relative">
                <img
                  src={item.previewUrl}
                  alt={item.file.name}
                  className="h-14 w-14 rounded-md object-cover border border-border"
                />
                <button
                  type="button"
                  onClick={() => removePending(index)}
                  className="absolute -top-1.5 -right-1.5 rounded-full bg-foreground text-background p-0.5 focus:outline-none focus:ring-2 focus:ring-ring"
                  aria-label={`Remove ${item.file.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              addFiles(event.target.files);
              event.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            aria-label="Attach screenshot"
          >
            <ImagePlus className="h-3.5 w-3.5" />
          </Button>
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Type a message…"
            rows={1}
            className={cn(
              // Override Textarea defaults (text-base / md:text-sm) to match bubble size.
              'min-h-8 py-1.5 text-xs md:text-xs resize-none overflow-y-auto',
              // Cap at 10 lines: padding (py-1.5 = 0.75rem) + text-xs line-height (1rem) × 10.
              'max-h-[calc(1rem*10+0.75rem)]',
            )}
            disabled={sending}
          />
          <Button
            type="button"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send message"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
